import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateNumeric } from "@/lib/format-amount";
import { cn } from "@/lib/utils";
import {
  LicenseFrequencyDTO,
  TeamLicenseInventoryDTO,
  TeamUserDTO,
} from "@shaarp/common";
import { TriangleAlertIcon } from "lucide-react";

type LicenseTotals = {
  purchased: number;
  assigned: number;
  available: number;
};

type ScheduledRemoval = {
  seats: number;
  effectiveAt: Date | null;
};

/**
 * Seats the pending decrease drops at the next renewal, across both
 * frequencies. A pending cancellation drops the whole pool, so it is treated as
 * a decrease down to zero.
 */
const computeScheduledRemoval = (
  inventory: TeamLicenseInventoryDTO
): ScheduledRemoval | null => {
  let seats = 0;
  let effectiveAt: Date | null = null;

  const collect = (data: LicenseFrequencyDTO) => {
    const purchased = data.pro.purchased;
    const effective = data.cancelAtPeriodEnd ? 0 : data.scheduledPro;
    if (effective === null || effective >= purchased) return;
    seats += purchased - effective;
    const date = data.currentPeriodEndsAt
      ? new Date(data.currentPeriodEndsAt)
      : null;
    if (date && (!effectiveAt || date < effectiveAt)) effectiveAt = date;
  };

  collect(inventory.monthly);
  collect(inventory.annual);

  return seats > 0 ? { seats, effectiveAt } : null;
};

const computeTotals = (inventory: TeamLicenseInventoryDTO): LicenseTotals => {
  const { monthly: m, annual: a } = inventory;
  return {
    purchased: m.pro.purchased + a.pro.purchased,
    assigned: m.pro.assigned + a.pro.assigned,
    available: m.pro.available + a.pro.available,
  };
};

type StatBlockProps = {
  label: string;
  children: React.ReactNode;
};

const StatBlock = ({ label, children }: StatBlockProps) => (
  <div className="flex flex-col gap-1.5 bg-card px-6 py-4">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    {children}
  </div>
);

type OccupancyBarProps = {
  assigned: number;
  total: number;
};

const OccupancyBar = ({ assigned, total }: OccupancyBarProps) => {
  const assignedPct = total > 0 ? Math.min((assigned / total) * 100, 100) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
      <div
        className="h-full rounded-full bg-majorelle transition-all"
        style={{ width: `${assignedPct}%` }}
      />
    </div>
  );
};

/**
 * Compact placeholder that mirrors the summary card's header and stats band so
 * the layout doesn't shift once the inventory resolves.
 */
export const CardLicenseSummarySkeleton = () => (
  <Card className="overflow-hidden p-0">
    <div className="space-y-2 px-6 pt-5 pb-4">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3.5 w-56" />
    </div>
    <div className="grid gap-px border-t bg-border sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, idx) => (
        <div key={idx} className="space-y-2 bg-card px-6 py-4">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-6 w-16" />
        </div>
      ))}
    </div>
  </Card>
);

type CardLicenseSummaryProps = {
  inventory: TeamLicenseInventoryDTO;
  /** Team members, used to name who loses a seat at the next renewal. */
  users?: TeamUserDTO[];
};

/**
 * License overview shown on the team users tab: total capacity, assigned seats
 * and remaining availability. Renders nothing when the team owns no licenses.
 */
export const CardLicenseSummary = ({
  inventory,
  users,
}: CardLicenseSummaryProps) => {
  const { t, i18n } = useTranslation();
  const totals = computeTotals(inventory);
  const scheduledRemoval = computeScheduledRemoval(inventory);
  const membersLosingSeat = (users ?? []).filter(
    (user) => user.scheduledUnassignAt
  );

  if (totals.purchased === 0) return null;

  const breakdown = (
    [
      { count: inventory.monthly.pro.purchased, key: "proMonthly" },
      { count: inventory.annual.pro.purchased, key: "proAnnual" },
    ] as const
  )
    .filter((item) => item.count > 0)
    .map((item) =>
      t(`team.licenseManagement.capacityBreakdown.${item.key}`, {
        count: item.count,
      })
    );

  return (
    <Card className="overflow-hidden p-0">
      <div className="space-y-1.5 px-6 pt-5 pb-4">
        <h2 className="text-base font-semibold leading-tight">
          {t("team.users.cards.licenses.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("team.users.cards.licenses.description")}
        </p>
      </div>

      <div className="grid gap-px border-t bg-border sm:grid-cols-3">
        <StatBlock label={t("team.licenseManagement.totalCapacityLabel")}>
          <span className="text-2xl font-bold tabular-nums leading-none">
            {totals.purchased}
          </span>
          {breakdown.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {breakdown.join(" · ")}
            </span>
          )}
        </StatBlock>

        <StatBlock label={t("team.licenseManagement.assignedLabel")}>
          <span className="text-2xl font-bold tabular-nums leading-none">
            {totals.assigned}
          </span>
          <OccupancyBar assigned={totals.assigned} total={totals.purchased} />
        </StatBlock>

        <StatBlock label={t("team.licenseManagement.availableLabel")}>
          <span
            className={cn(
              "text-2xl font-bold tabular-nums leading-none",
              totals.available > 0
                ? "text-green-600 dark:text-green-400"
                : "text-muted-foreground"
            )}
          >
            {totals.available}
          </span>
        </StatBlock>
      </div>

      {scheduledRemoval && (
        <div className="flex items-start gap-2 border-t bg-destructive/5 px-6 py-3">
          <TriangleAlertIcon className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-destructive">
              {t("team.licenseManagement.scheduledRemovalNotice", {
                count: scheduledRemoval.seats,
                date: formatDateNumeric(
                  scheduledRemoval.effectiveAt,
                  i18n.language
                ),
              })}
            </p>
            <p className="text-xs text-muted-foreground">
              {membersLosingSeat.length > 0
                ? t("team.licenseManagement.scheduledRemovalMembers", {
                    names: membersLosingSeat
                      .map((user) => user.fullName?.trim() || user.email)
                      .join(", "),
                  })
                : t("team.licenseManagement.scheduledRemovalUnassignedSeats")}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};
